/**
 * Assemble complete LLM prompts from components.
 *
 * Combines system prompt template + serialized registry + workspace state
 * + few-shot examples, with token budget enforcement.
 *
 * @module gibbs/text-to-action/buildPrompt
 */

import { serializeRegistryContext, estimateTokens } from './serializeRegistry';
import { serializeWorkspace } from './serializeWorkspace';
import { filterForCommandTier, filterForWorkflowTier } from './filterActions';
import {
    commandTierSystemPrompt,
    workflowTierSystemPrompt,
    commandTierExamples,
    workflowTierExamples,
} from './promptTemplates';
import { findRelevantRules, formatRulesForPrompt } from '../knowledge/medchemRules';
import { getActiveFrame } from '../question-chooser';

/**
 * Token budget limits per tier.
 * Reserve space for user message (~500 tokens), response (~2000 tokens),
 * and user context (~500 tokens for preferences + history).
 */
const TOKEN_BUDGETS = {
    command: 11500, // gpt-3.5-turbo 16k minus reserves (includes ~500 for user context)
    workflow: 99500, // o3-mini 128k minus reserves (includes ~500 for user context)
};

/**
 * Maximum token budgets for documentation chunks per tier.
 */
const DOC_TOKEN_BUDGETS = {
    command: 2000,
    workflow: 15000,
};

/**
 * Format the Meanwell medchem-rule section when the user's query looks
 * design-intent-y (mentions isosteres, fluorine, toxicophores, specific rings,
 * etc.). Returns '' when no rule is relevant — no tokens wasted.
 */
function formatMedchemRuleSection(userMessage) {
    const rules = findRelevantRules(userMessage, 5);
    if (!rules || rules.length === 0) return '';
    return `\n\n${ formatRulesForPrompt(rules)}`;
}

/**
 * Format the decision-frame section (gibbs-question-chooser). When the
 * user has picked a frame, surface its promptPrefix at the top of the
 * system message so the LLM biases tool selection toward the frame's
 * relevant actions. Returns '' when no frame is active.
 *
 * @returns {string} formatted section or empty string
 */
function formatActiveFrameSection() {
    const frame = getActiveFrame();
    if (!frame || !frame.promptPrefix) return '';
    return `\n\n## Decision Frame\n${frame.promptPrefix}`;
}

/**
 * Format the anchored-target section when the upstream anchor resolver
 * has verified a specific target. Surfacing this in the system prompt
 * keeps the LLM grounded on the user's actual target choice and prevents
 * the few-shot-mimicry / workspace-misuse failure modes that motivated
 * gibbs-clarify-on-ambiguous-target.
 *
 * @param {Object|null} anchoredProtein - { pdbId?, name?, uniprot? }
 * @returns {string} formatted section or empty string
 */
function formatAnchoredTargetSection(anchoredProtein) {
    if (!anchoredProtein) return '';
    const label = anchoredProtein.name || anchoredProtein.pdbId
        || anchoredProtein.uniprot || null;
    if (!label) return '';
    const pdb = anchoredProtein.pdbId ? ` (PDB ${anchoredProtein.pdbId})` : '';
    const uniprot = anchoredProtein.uniprot ? ` (UniProt ${anchoredProtein.uniprot})` : '';
    return '\n\n## Anchored Target\n'
        + `The user's stated target for this workflow is **${label}**${pdb}${uniprot}.\n`
        + 'Use this as the anchor for any plan you produce — do NOT propose '
        + 'loading a different protein or substituting a target from a few-shot example.';
}

/**
 * Format the user context section (preferences + history) for prompt injection.
 *
 * @param {Object} [userContext] - { preferences: string, history: string }
 * @returns {string} Formatted user context section, or empty string if absent
 */
function formatUserContextSection(userContext) {
    if (!userContext) return '';
    const { preferences, history } = userContext;
    if (!preferences && !history) return '';

    const parts = ['\n\n## User Context'];
    if (preferences) parts.push(preferences);
    if (history) parts.push(history);
    return parts.join('\n');
}

/**
 * Format documentation chunks into a prompt section, respecting token budget.
 *
 * Chunks are assumed to be sorted by score (highest first) from the retriever.
 * Lowest-scored chunks are dropped first if the total exceeds the budget.
 *
 * @param {Array<{text: string, source: string, score: number}>} docChunks
 * @param {string} tier - 'command' | 'workflow'
 * @returns {string} Formatted documentation section, or empty string if no chunks
 */
function formatDocSection(docChunks, tier) {
    if (!docChunks || docChunks.length === 0) return '';

    const budget = DOC_TOKEN_BUDGETS[tier] || DOC_TOKEN_BUDGETS.command;
    const header = '\n\n## Relevant Documentation\nThe following documentation may be relevant to the user\'s request. Use your judgment about applicability.\n';
    const headerTokens = estimateTokens(header);
    let totalTokens = headerTokens;
    const included = [];

    for (const chunk of docChunks) {
        const entry = `\n### ${chunk.source}\n${chunk.text}\n`;
        const entryTokens = estimateTokens(entry);

        if (totalTokens + entryTokens > budget) break;

        totalTokens += entryTokens;
        included.push(entry);
    }

    if (included.length === 0) return '';

    return header + included.join('');
}

/**
 * Build a complete prompt for the command tier (single-action requests).
 *
 * @param {string} userMessage - The user's natural language input
 * @param {Object} workspace - BMaps workspace state
 * @param {Array} [docChunks=[]] - Retrieved documentation chunks
 * @param {Object} [userContext] - { preferences, history } from memory
 * @returns {Object} { messages, model, estimatedTokens }
 */
export function buildCommandPrompt(
    userMessage, workspace, docChunks = [], userContext, cached = {}
) {
    const workspaceState = cached.workspaceState || serializeWorkspace(workspace);
    const actions = cached.actions || filterForCommandTier(workspace, userMessage);
    const registryContext = serializeRegistryContext(actions, 'compact');
    const docSection = formatDocSection(docChunks, 'command');
    const userContextSection = formatUserContextSection(userContext);

    const medchemSection = formatMedchemRuleSection(userMessage);
    const anchorSection = formatAnchoredTargetSection(cached.anchoredProtein);
    const frameSection = formatActiveFrameSection();
    const systemContent = commandTierSystemPrompt(registryContext, workspaceState)
        + frameSection + userContextSection + anchorSection
        + medchemSection + docSection;
    const examples = commandTierExamples();

    const messages = [
        { role: 'system', content: systemContent },
        ...examples,
        { role: 'user', content: userMessage },
    ];

    const estimated = estimateTokens({ system: systemContent, examples, user: userMessage });

    if (estimated > TOKEN_BUDGETS.command) {
        // Reduce action count to fit
        const reducedActions = actions.slice(0, 15);
        const reducedContext = serializeRegistryContext(reducedActions, 'compact');
        const reducedSystem = commandTierSystemPrompt(reducedContext, workspaceState) + docSection;
        messages[0] = { role: 'system', content: reducedSystem };
    }

    return {
        messages,
        model: 'gpt-3.5-turbo',
        estimatedTokens: estimated,
    };
}

/**
 * Build a complete prompt for the workflow tier (multi-step planning).
 *
 * @param {string} userMessage - The user's natural language input
 * @param {Object} workspace - BMaps workspace state
 * @param {Array} [docChunks=[]] - Retrieved documentation chunks
 * @param {Object} [userContext] - { preferences, history } from memory
 * @returns {Object} { messages, model, estimatedTokens }
 */
export function buildWorkflowPrompt(
    userMessage, workspace, docChunks = [], userContext, cached = {}
) {
    const workspaceState = cached.workspaceState || serializeWorkspace(workspace);
    const actions = cached.actions || filterForWorkflowTier(userMessage);
    const registryContext = serializeRegistryContext(actions, 'full');
    const docSection = formatDocSection(docChunks, 'workflow');
    const userContextSection = formatUserContextSection(userContext);

    const medchemSection = formatMedchemRuleSection(userMessage);
    const anchorSection = formatAnchoredTargetSection(cached.anchoredProtein);
    const frameSection = formatActiveFrameSection();
    const systemContent = workflowTierSystemPrompt(registryContext, workspaceState)
        + frameSection + userContextSection + anchorSection
        + medchemSection + docSection;
    const examples = workflowTierExamples();

    const messages = [
        { role: 'system', content: systemContent },
        ...examples,
        { role: 'user', content: userMessage },
    ];

    const estimated = estimateTokens({ system: systemContent, examples, user: userMessage });

    return {
        messages,
        model: 'o3-mini',
        estimatedTokens: estimated,
    };
}
